import * as React from "react";
import { SurveysTemplate } from "../../../template/surveys/default-template";
import { useHistory, useLocation } from "react-router-dom";
import {
  SurveyDetailService,
  SurveyDetailServiceProps,
} from "../../../../application/service/surveys/survey-detail/survey-detail-service";
import { SurveyR } from "../route";
import { Stats } from "./tabs/stats";
import { FormSetting } from "./tabs/form-setting";
import { Results } from "./tabs/results";
import { useParams } from "react-router";
import { useEffect, useMemo, useCallback } from "react";
import { useModal } from "../../../../utils/custom-hooks";
import { GroupShareModal } from "../../../organisms/common/group-share-modal";
import { useAuthContextV2 } from "../../../../auth-state";
import {
  connectPageService,
  SurveyStatusTag,
  SurveyTabState,
  BaseModal,
  CopySurveyModal,
} from "@graffer-inc/smart-apply";
import { MenuButtons } from "./menu-buttons";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";
import { PublishSettingModal } from "../../../organisms/common/publish-setting-modal";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { Tabs } from "@graffer-inc/console-design-kit";
import { useFormUnsaved } from "../../../../utils/use-form-unsaved";

const SurveysDetailPage: React.FC<SurveyDetailServiceProps> = ({
  tabState,
  switchTab,
  survey,
  stats,
  results,
  tableData,
  setDateRange,
  dateRange,
  onResultsRequested,
  paging,
  selectedMetaIds,
  requireSelectableRawValue,
  setPage,
  movePage,
  onMount,
  onSave,
  onDelete,
  onUnPublish,
  onPauseAccepting,
  onResumeAccepting,
  onPublishSettingSave,
  onCSVDownload,
  onGroupUpdated,
  onReturnToDraft,
  onCopy,
  onExportSurvey,
  onSelectCsvMeta,
  onChangeRequireSelectableRawValue,
  formatFileResourceURL,
  onFileBulkDownload,
  onDeleteSurveyAnswers,
  csvEncoding,
  onChangeCSVEncoding,
  resetCSVModalState,
  onOpenPreview,
}) => {
  const { id } = useParams<{ id: string }>();
  const { tab } = parseQueryOfLocation(useLocation()) as {
    tab: string | undefined;
  };
  const history = useHistory();
  const authState = useAuthContextV2();
  const shareModal = useModal();
  const returnToDraftModal = useModal();
  const publishSettingModal = useModal();
  const unpublishConfirmModal = useModal();
  const deleteConfirmModal = useModal();
  const pauseAcceptingModal = useModal();
  const resumeAcceptingModal = useModal();
  const copyModal = useModal();
  const exportSurveyModal = useModal();
  const authCtx = useAuthContextV2();
  const { setIsFormUnsaved, handleSwitchTab, confirmWhenFormUnsaved } =
    useFormUnsaved({
      tabState,
      switchTab,
      object: survey,
      product: "SURVEY",
    });
  const handleOnGoToParentPage = useCallback(
    (path: string) => {
      confirmWhenFormUnsaved<{ path: string }>(
        ({ path }) => {
          history.push(path);
        },
        { path },
      );
    },
    [confirmWhenFormUnsaved, history],
  );

  useEffect(() => {
    onMount(id, tab);
  }, [onMount, id, tab]);

  const canDeleteAnswers = useMemo(() => {
    if (!survey) return false;
    return results.length > 0 && survey.publishStatus === "UNPUBLISHED";
  }, [results, survey]);

  if (!survey) {
    return null;
  }

  const canUpdateSurvey = authState.auth.checkGroupPermission(survey, [
    "surveys.update",
  ]);
  const isSafeEditOnly = survey.isPublished();

  return (
    <SurveysTemplate
      menuLinkOverrideAction={handleOnGoToParentPage}
      breadcrumbs={[
        { to: SurveyR.Surveys.link({}), title: "アンケート一覧" },
        { to: "#", title: survey?.name },
      ]}
    >
      <div className="columns is-vcentered" style={{ marginBottom: "0px" }}>
        <div className="column has-text-left">
          <h1 className="title">
            <i className="fa fa-poll" /> {survey?.name}
          </h1>
        </div>
        {!!authState.auth.data?.orgSlug && (
          <div className="column has-text-right is-narrow">
            <MenuButtons
              survey={survey}
              orgSlug={authState.auth.data?.orgSlug}
              onManageGroupOpen={shareModal.open}
              onPublishSettingModalOpen={publishSettingModal.open}
              onDelete={deleteConfirmModal.open}
              onUnPublish={unpublishConfirmModal.open}
              onReturnToDraft={returnToDraftModal.open}
              onPauseAccepting={pauseAcceptingModal.open}
              onResumeAccepting={resumeAcceptingModal.open}
              onCopyOpen={copyModal.open}
              onExportSurvey={exportSurveyModal.open}
              onOpenPreview={onOpenPreview}
            />
          </div>
        )}
      </div>
      <div style={{ margin: "0 0 10px 0" }}>
        <SurveyStatusTag surveyStatus={survey.publishStatus} />
      </div>
      <div className="columns">
        <div className="column">
          <Tabs
            tabItems={[
              {
                label: "基本情報",
                isActive: tabState.equals("STATS"),
                onClick: () => handleSwitchTab(SurveyTabState.initialize()),
                hidden: false,
              },
              {
                label: "フォーム設定",
                isActive: tabState.equals("FORM"),
                onClick: () => handleSwitchTab(SurveyTabState.form()),
                hidden: false,
              },
              {
                label: "アンケートデータ",
                isActive: tabState.equals("RESULTS"),
                onClick: () => handleSwitchTab(SurveyTabState.results()),
                hidden: false,
              },
            ]}
          />
        </div>
      </div>
      <div className="columns">
        <div className="column">
          {tabState.matchType({
            STATS: survey && stats && !!authCtx.auth.data?.orgSlug && (
              <Stats
                survey={survey!}
                stats={stats!}
                onSurveyUpdate={(s) => {
                  onSave(s);
                }}
                orgSlug={authCtx.auth.data?.orgSlug}
                canUpdateSurvey={canUpdateSurvey}
                canReadStats={authState.auth.checkGroupPermission(survey, [
                  "surveys.read_stats",
                ])}
              />
            ),
            FORM: (
              <FormSetting
                disabled={false}
                survey={survey!}
                onSurveyUpdate={(s) => {
                  onSave(s);
                }}
                canUpdateSurvey={canUpdateSurvey}
                isSafeEditOnly={isSafeEditOnly}
                setIsFormUnsaved={setIsFormUnsaved}
              />
            ),
            RESULTS: (
              <Results
                onCSVDownload={onCSVDownload}
                dateRange={dateRange}
                setDateRange={setDateRange}
                tableData={tableData()}
                canDownloadSurveyCsv={authState.auth.checkGroupPermission(
                  survey,
                  ["surveys.download_csv"],
                )}
                fetchResults={onResultsRequested}
                paging={paging}
                setPage={setPage}
                movePage={movePage}
                selectable={{
                  selectedMetaIds: selectedMetaIds,
                  onSelectCsvMeta: onSelectCsvMeta,
                }}
                requireSelectableRawValue={requireSelectableRawValue}
                onChangeRequireSelectableRawValue={
                  onChangeRequireSelectableRawValue
                }
                formatFileResourceURL={formatFileResourceURL}
                onFileBulkDownload={onFileBulkDownload}
                canDeleteAnswers={canDeleteAnswers}
                onDelete={onDeleteSurveyAnswers}
                csvEncoding={csvEncoding}
                onChangeCSVEncoding={onChangeCSVEncoding}
                resetCSVModalState={resetCSVModalState}
              />
            ),
          })}
        </div>
      </div>
      {shareModal.isActive && (
        <GroupShareModal
          onClose={shareModal.close}
          onAdd={onGroupUpdated}
          sharedGroupIds={survey.groupIds}
        />
      )}
      <BaseModal
        isActive={deleteConfirmModal.isActive}
        title="アンケートを削除"
        onClose={deleteConfirmModal.close}
      >
        <section className="modal-card-body">
          <div className="is-small">
            アンケートを削除してよろしいでしょうか。
          </div>
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-failure is-fullwidth"
            onClick={deleteConfirmModal.close}
          >
            キャンセル
          </button>
          <button
            className="button is-info is-fullwidth"
            onClick={async () => {
              await onDelete();
              deleteConfirmModal.close();
              history.push(SurveyR.Surveys.link({}));
            }}
          >
            削除する
          </button>
        </footer>
      </BaseModal>
      <PublishSettingModal
        isActive={publishSettingModal.isActive}
        publishSetting={survey.publishSetting}
        publishStatus={survey.publishStatus}
        isPublished={survey.isPublished()}
        onPublishSettingUpdate={onPublishSettingSave}
        onClose={publishSettingModal.close}
      />
      <BaseModal
        title={"アンケートの公開停止"}
        isActive={unpublishConfirmModal.isActive}
        onClose={unpublishConfirmModal.close}
      >
        <section className="modal-card-body">
          <p>
            公開停止にすると回答を受けつけることができなくなります。よろしいですか？
          </p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={unpublishConfirmModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onUnPublish();
              unpublishConfirmModal.close();
            }}
          >
            公開停止にする
          </button>
        </section>
      </BaseModal>
      <ConfirmModal
        isActive={returnToDraftModal.isActive}
        title="下書きに戻す"
        body="下書きに戻すとすべての項目の更新が可能になります。"
        successButtonText="戻す"
        onSuccess={() => {
          onReturnToDraft();
          returnToDraftModal.close();
        }}
        onClose={returnToDraftModal.close}
      />
      <BaseModal
        title={"アンケート受付の一時停止"}
        isActive={pauseAcceptingModal.isActive}
        onClose={pauseAcceptingModal.close}
      >
        <section className="modal-card-body">
          <p>
            受付を一時停止すると再開するまで申請を受けつけることができなくなります。よろしいですか？
          </p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={pauseAcceptingModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onPauseAccepting();
              pauseAcceptingModal.close();
            }}
          >
            受付を一時停止する
          </button>
        </section>
      </BaseModal>
      <BaseModal
        title={"アンケート受付の再開"}
        isActive={resumeAcceptingModal.isActive}
        onClose={resumeAcceptingModal.close}
      >
        <section className="modal-card-body">
          <p>受付を再開します。よろしいですか？</p>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={resumeAcceptingModal.close}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button is-fullwidth is-danger"
            onClick={() => {
              onResumeAccepting();
              resumeAcceptingModal.close();
            }}
          >
            受付を再開する
          </button>
        </section>
      </BaseModal>
      {survey && copyModal.isActive && (
        <CopySurveyModal
          onCopy={onCopy}
          survey={survey}
          onClose={copyModal.close}
          isActive={copyModal.isActive}
        />
      )}
      <ConfirmModal
        isActive={exportSurveyModal.isActive}
        title="アンケートをエクスポート"
        body="このアンケートの設定をファイルとしてエクスポートします。"
        successButtonText="エクスポート"
        onSuccess={async () => {
          await onExportSurvey();
          exportSurveyModal.close();
        }}
        onClose={exportSurveyModal.close}
      />
    </SurveysTemplate>
  );
};

export const SurveyDetailsContainer = connectPageService(
  SurveysDetailPage,
  () => new SurveyDetailService(),
);
